import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster, ToastBar } from 'react-hot-toast';
import DismissibleToastContent from './components/UI/DismissibleToastContent';

// Configure TanStack Query optimized for HR Portal
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on 401/403 (auth issues) or 404 (not found)
        if (error?.response?.status === 401 || error?.response?.status === 403 || error?.response?.status === 404) {
          return false;
        }
        return failureCount < 2; // Retry up to 2 times for other errors
      },
      staleTime: 5 * 60 * 1000, // 5 minutes - HR data doesn't change super frequently
      gcTime: 10 * 60 * 1000, // 10 minutes cache time (formerly cacheTime)
      refetchOnWindowFocus: false, // Disable to reduce API load when switching tabs
      refetchOnMount: false, // Disable to reduce API load on component mount
      refetchInterval: false, // No automatic polling by default
    },
    mutations: {
      retry: (failureCount, error) => {
        const status = error?.response?.status;
        // Never retry client errors (4xx) — validation failures, auth errors,
        // and other request issues won't succeed on retry.
        if (status >= 400 && status < 500) {
          return false;
        }
        return failureCount < 1; // Only retry once for server errors
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <AuthProvider>
        <App />
        <Toaster 
          position="top-right"
          containerClassName="toast-container"
          containerStyle={{ zIndex: 10050 }}
          toastOptions={{
            duration: 4000,
            className: 'toast',
            success: {
              className: 'toast toast-success',
              duration: 4000,
            },
            error: {
              className: 'toast toast-error',
              duration: 6000,
            },
            loading: {
              className: 'toast toast-loading',
            },
            // Custom toast types for info and warning
            custom: {
              className: 'toast toast-info',
              duration: 5000,
            },
            style: {
              padding: '8px 16px',
              margin: '0',
              boxShadow: 'none',
              // Adaptive shape: pill for short messages, rounded card for long
              // wrapping ones (see .toast rules in index.css for rationale).
              borderRadius: '20px',
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px',
              borderBottomLeftRadius: '20px',
              borderBottomRightRadius: '20px',
              // REASONABLE DIMENSIONS
              minWidth: '200px',
              maxWidth: '360px',
              minHeight: '36px',
              fontSize: '14px',
            },
          }}
        >
          {(t) => (
            <ToastBar toast={t}>
              {({ icon, message }) => (
                <DismissibleToastContent t={t} icon={icon} message={message} />
              )}
            </ToastBar>
          )}
        </Toaster>
      </AuthProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

// ── Prevent pinch-to-zoom in PWA standalone mode (iOS + Android) ──
// iOS/WebKit: gesturestart/gesturechange/gestureend (no-op on Android)
document.addEventListener('gesturestart', (e) => e.preventDefault());
document.addEventListener('gesturechange', (e) => e.preventDefault());
document.addEventListener('gestureend', (e) => e.preventDefault());
// Android/Chrome: block multi-touch zoom via standard touch events
document.addEventListener('touchstart', (e) => { if (e.touches.length > 1) e.preventDefault(); }, { passive: false });
document.addEventListener('touchmove', (e) => { if (e.touches.length > 1) e.preventDefault(); }, { passive: false });

// Register service worker for PWA functionality (only in production)
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
      .then((registration) => {
        console.log('SW registered: ', registration);
      })
      .catch((registrationError) => {
        console.log('SW registration failed: ', registrationError);
      });
  });
}

// Check for app updates (removed auto-reload to prevent infinite refresh loops) 